/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - Munich, London 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [update-account.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 03.01.20, 09:01
 */

import React, {Fragment, useEffect, useState} from 'react';
import axios from "axios";
import {
    BILLABLE,
    CHARGES_NONE, CLUB_MEMBERSHIP,
    EVENT_SPACE, FREE_MEMBERSHIP,
    FREE_OF_CHARGE,
    GENERIC_SPACE, GOLD_MEMBERSHIP, GROUP_SPACE,
    HYGIENE_LEGITIMATE,
    HYGIENE_NONE,
    MENU_SPACE, ONLY_ME_ACCESS, PLATINUM_MEMBERSHIP,
    PRIVATE_ACCESS,
    PUBLIC_ACCESS, REGULAR_MEMBERSHIP,
    RESTRICTED_ACCESS, SILVER_MEMBERSHIP, SPACE_SUBTYPE_GROUP, SPACE_SUBTYPE_MENU, VIP_MEMBERSHIP
} from "../../../actions/spaces";

import {useConfiguration} from "../../configuration/configuration";
import {Button, RoundedButton} from "../../navigation/buttons/nav-buttons";
import DateTimeEditor from "./date-time-editor";
import {getCreateSpaceUrl, getUpdateSpaceUrl} from "../../../actions/environment";
import {authConfig} from "../../../actions/local-storage";
import {
    AddressFormData,
    CalendarDate,
    populateClientAddressData,
    populateClientCalendarDates,
    populateClientFormData,
    populateServerGenericData,
    SpacesFormData
} from "./manage-space-model";
import {CheckCircle, ReportProblem, Today} from "@material-ui/icons";
import {NavigationGroup, NavigationRow} from "../../navigation/layout/nav-layout";
import {SpinnerBig} from "../../util/spinner";
import {cn} from "../../util/text-utils";
import {ERROR_STATUS, NONE_STATUS, PENDING_STATUS, SUCCESS_STATUS} from "../../../actions";
import FormMarkdownTextarea from "../../toggler/form-markdown-textarea";


// helper
export const FormRow = ({className, border, left, children}) => {
    return <div
        className={cn('row', [border, 'standard-form-border'], [left, 'float-left'], [className])}>
        <div className={`col-md-12`}>
            {children}
        </div>
    </div>
}

// helper
export const FormRowHeader = ({className, border, left, children}) => {
    return <div
        className={cn('row mb-2', [border, 'standard-form-border'], [left, 'float-left'], [className])}>
        {children}
    </div>
}

const ManageSpaceForm = ({authorization, spaceId, copy = false, genericData, onCreated, onUpdated}) => {
    const [formData, setFormData] = useState(SpacesFormData);
    const [addressData, setAddressData] = useState(AddressFormData);
    const [calendarDates, setCalendarDates] = useState([]);
    const [status, setStatus] = useState(NONE_STATUS);
    const [invalid, setInvalid] = useState(false);
    const [billable, setBillable] = useState(false);
    const [legitimate, setLegitimate] = useState(false);
    const [showMore, setShowMore] = useState(false);
    const [isMenu, setIsMenu] = useState(false);
    const [isGroup, setIsGroup] = useState(false);
    const {Copy, Lang} = useConfiguration();

    const populateFormData = (genericData) => {
        const {space, spacedata} = genericData;

        if (copy) space.name = Lang.updateSpace.copyOfSuffix(space.name);
        setFormData(populateClientFormData(formData, space, spacedata));
        setCalendarDates(populateClientCalendarDates(calendarDates, spacedata));
        setBillable(space.fees === BILLABLE);
        setLegitimate(space.hygiene === HYGIENE_LEGITIMATE)


        if (space.type === GENERIC_SPACE && space.subType === SPACE_SUBTYPE_GROUP) {
            setIsGroup(true);
        }

        if (space.type === GENERIC_SPACE && space.subType === SPACE_SUBTYPE_MENU) {
            setIsMenu(true);
        }

        if (spacedata.theVenue || spacedata.accommodation || spacedata.travelInformation) {
            setShowMore(true);
        }

        if (spacedata.address) {
            setAddressData(populateClientAddressData(addressData, spacedata.address));
        }

    }

    const updateSpace = () => {
        const genericData = populateServerGenericData(formData, calendarDates, addressData);

        setStatus(PENDING_STATUS);
        axios.post(getUpdateSpaceUrl(authorization.principal.username, spaceId), genericData, authConfig())
        .then(response => {
            onUpdated && onUpdated(response.data);
            setStatus(SUCCESS_STATUS);
        })
        .catch(error => {
            console.log('UpdateSpace error', error);
            setStatus(ERROR_STATUS);
        })
    }

    const createSpace = () => {
        const genericData = populateServerGenericData(formData, calendarDates, addressData);
        setStatus(PENDING_STATUS);
        axios.post(getCreateSpaceUrl(authorization.principal.username), genericData, authConfig())
        .then(response => {
            onCreated && onCreated(response.data);
            setStatus(SUCCESS_STATUS);
        })
        .catch(error => {
            console.log('createSpace error', error);
            setStatus(ERROR_STATUS);
        })
    }

    useEffect(() => {
        spaceId && genericData && populateFormData(genericData);
    }, [spaceId])

    const handleSubmit = (event) => {
        event.target.classList.add('was-validated');
        event.preventDefault();
        event.stopPropagation();

        if (!event.target.checkValidity()) {
            setInvalid(true);
            return;
        }
        setInvalid(false);

        if (spaceId && !copy) {
            updateSpace();
        } else {
            createSpace();
        }
    }

    const handleOnChange = (event) => {
        setFormData({...formData, [event.target.name]: event.target.value});
    }

    const handleOnTypeChange = (event) => {
        const isMenuSpace = event.target.value === MENU_SPACE;
        const isGroupSpace = event.target.value === GROUP_SPACE;
        setIsMenu(isMenuSpace);
        setIsGroup(isGroupSpace);
        if (isMenuSpace) {
            setFormData({
                ...formData, [event.target.name]: event.target.value,
                ['access']: RESTRICTED_ACCESS,
                ['fees']: CHARGES_NONE,
                ['hygiene']: HYGIENE_NONE,
            });
        } else if (isGroupSpace) {
            setFormData({
                ...formData, [event.target.name]: event.target.value,
                ['fees']: CHARGES_NONE,
                ['hygiene']: HYGIENE_NONE,
            });

        } else {
            setFormData({
                ...formData, [event.target.name]: event.target.value,
                ['access']: null,
                ['fees']: null,
                ['hygiene']: null,
            });
        }
    }

    const handleOnChangeAddress = (event) => {
        setAddressData({...addressData, [event.target.name]: event.target.value});
    }

    const handleOnBillable = (event) => {
        setBillable(event.target.value === BILLABLE);
        setFormData({
            ...formData,
            [event.target.name]: event.target.value,
            ['feesDescription']: ''
        });              // resets description to empty
    }

    const handleOnHygiene = (event) => {
        setLegitimate(event.target.value === HYGIENE_LEGITIMATE);
        setFormData({
            ...formData,
            [event.target.name]: event.target.value,
            ['hygieneDescription']: ''
        });    // resets description to empty
    }

    const handleOnNewDate = event => {
        event.preventDefault();
        if (calendarDates.length > 0) {

        }
        setCalendarDates([...calendarDates, getNewCalendarDate()]);
    }

    const getNewCalendarDate = () => {
        if (calendarDates.length === 0) {
            return CalendarDate(new Date());
        } else {
            const previous = calendarDates[calendarDates.length - 1];
            return CalendarDate(previous.date, previous.startTime, previous.endTime);
        }
    }

    const handleOnDeleteDatum = id => event => {
        event.preventDefault();
        const filteredDates = calendarDates.filter((date, index) => index !== id)
        setCalendarDates(filteredDates);
    }

    const handleOnChangeDateTime = (name, id) => date => {
        calendarDates[id] = {...calendarDates[id], [name]: date};
        setCalendarDates([...calendarDates]);
    }

    const handleOnChangeDescription = (name, id) => event => {
        const description = event.target.value;
        calendarDates[id] = {...calendarDates[id], [name]: description};
        setCalendarDates([...calendarDates]);
    }

    const handleOnChangeName = (name, id) => event => {
        const calendarName = event.target.value;
        calendarDates[id] = {...calendarDates[id], [name]: calendarName};
        setCalendarDates([...calendarDates]);
    }

    const renderCalendarDates = () => {
        return calendarDates
        // .sort((left, right) => (left.date + left.startTime) < (right.date + right.startTime) ? -1 : 1)
        .map((calendarDate, index) => {
            return <DateTimeEditor
                key={index}
                date={calendarDate.date}
                from={calendarDate.startTime}
                until={calendarDate.endTime}
                description={calendarDate.description}
                name={calendarDate.name}
                onDelete={handleOnDeleteDatum(index)}
                onChangeDate={handleOnChangeDateTime('date', index)}
                onChangeFrom={handleOnChangeDateTime('startTime', index)}
                onChangeUntil={handleOnChangeDateTime('endTime', index)}
                onChangeDescription={handleOnChangeDescription('description', index)}
                onChangeName={handleOnChangeName('name', index)}/>
        })
    }

    const reportProblem = () => {
        return <div>
            <span className='form-btn-error mt-4'><ReportProblem className='font-3x'/></span>
            <div className='mt-4 mb-4 text-center'>{Lang.updateSpace.reportProblem}</div>
        </div>
    }

    if (spaceId && !genericData) return reportProblem();
    const isNew = !spaceId;
    const isEvent = genericData && genericData.space.type === 'EVENT';


    if (status !== NONE_STATUS) {
        return <div className='standard-form-container'>
            <div className='standard-form standard-form-table'>
                <h3 className='m-0'>{Copy.navigation.fullName}</h3>
                <h4 className="mt-1">{isNew ? Lang.updateSpace.title.new
                    : isEvent ? Lang.updateSpace.title.event(genericData.space.name)
                        : Lang.updateSpace.title.space(genericData.space.name)}</h4>
                {status === PENDING_STATUS && <div>
                    <SpinnerBig className='font-3x mt-4'/>
                    <div className='mt-4 mb-4 text-center'>{Lang.updateSpace.updateRequest}</div>
                </div>}

                {status === SUCCESS_STATUS && <div>
                    <span className='form-btn-success mt-4'><CheckCircle className='font-3x'/></span>
                    <div className='mt-4 mb-4 text-center'>{Lang.updateSpace.success}</div>
                </div>}

                {status === ERROR_STATUS && reportProblem()}
            </div>
        </div>
    }

    return <div className='standard-form-container'>
        <h3 className="m-0">{Copy.navigation.fullName}</h3>
        <h4 className="mt-1">{isNew ? Lang.updateSpace.title.new
            : isEvent ? Lang.updateSpace.title.event(genericData.space.name)
                : Lang.updateSpace.title.space(genericData.space.name)}</h4>

        <form noValidate className={`needs-validation ${invalid ? 'form-invalid' : ''}`} onSubmit={handleSubmit}>

            {isNew && <React.Fragment>
                <label className="form-check-label float-left"
                       htmlFor="freeOfChargesId">{Lang.updateSpace.type.text}</label>
                <div style={{clear: 'both'}}/>
                <FormRow border className='text-left mx-0 mb-2'>
                    <div className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="type"
                               checked={formData.type === EVENT_SPACE}
                               onChange={handleOnTypeChange}
                               id="eventTypeId" value={EVENT_SPACE} required/>
                        <label className="form-check-label"
                               htmlFor="eventTypeId">{Lang.updateSpace.type.event}</label>
                    </div>

                    <div className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="type"
                               checked={formData.type === GENERIC_SPACE}
                               onChange={handleOnTypeChange}
                               id="genericTypeId" value={GENERIC_SPACE}/>
                        <label className="form-check-label"
                               htmlFor="genericTypeId">{Lang.updateSpace.type.generic}</label>
                    </div>

                    <div className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="type"
                               checked={formData.type === GROUP_SPACE}
                               onChange={handleOnTypeChange}
                               id="genericTypeId" value={GROUP_SPACE}/>
                        <label className="form-check-label"
                               htmlFor="genericTypeId">{Lang.updateSpace.type.group}</label>
                    </div>

                    <div className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="type"
                               checked={formData.type === MENU_SPACE}
                               onChange={handleOnTypeChange}
                               id="menuTypeId" value={MENU_SPACE}/>
                        <label className="form-check-label"
                               htmlFor="menuTypeId">{Lang.updateSpace.type.menu}</label>
                    </div>
                </FormRow>
                <div className="form-text text-muted text-left mb-2">
                    {Lang.updateSpace.type.info}
                </div>
            </React.Fragment>}

            <FormRowHeader className='mb-2'>
                <div className='col-md-9'>
                    <label className="form-check-label float-left"
                           htmlFor="nameId">{Lang.updateSpace.name.text}</label>
                    <input className='form-control' type="text" name="name"
                           id='nameId'
                           placeholder={Lang.updateSpace.name.placeholder}
                           value={formData.name}
                           onChange={handleOnChange} required/>
                </div>
                <div className='col-md-3'>
                    <label className="form-check-label float-left"
                           htmlFor="categoryId">{Lang.updateSpace.category.text}</label>
                    <input className='form-control' type="text" name="category"
                           id='categoryId'
                           placeholder={Lang.updateSpace.category.placeholder}
                           value={formData.category}
                           onChange={handleOnChange} required/>
                </div>
            </FormRowHeader>

            <FormMarkdownTextarea className='form-control' name="description"
                                  label={Lang.updateSpace.description.text}
                                  placeholder={Lang.updateSpace.description.placeholder}
                                  id='descriptionId'
                                  value={formData.description}
                                  maxLength='512'
                                  onChange={handleOnChange} required/>

            <FormMarkdownTextarea className="form-control" name="generalInformation"
                                  label={Lang.updateSpace.generalInformation.text}
                                  placeholder={Lang.updateSpace.generalInformation.placeholder}
                                  id='generalId'
                                  rows='12'
                                  value={formData.generalInformation}
                                  maxLength='10024'
                                  onChange={handleOnChange} required/>

            <div style={{clear: 'both'}}/>

            {/**** ACCESS ****/}
            {!isMenu && <React.Fragment>
                <label className="form-check-label float-left"
                       htmlFor="publicAccessId">{Lang.updateSpace.access.text}</label>
                <div style={{clear: 'both'}}/>
                <FormRow border className='text-left mx-0 mb-2'>
                    <div className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="access"
                               checked={formData.access === PUBLIC_ACCESS}
                               onChange={handleOnChange}
                               id="publicAccessId" value={PUBLIC_ACCESS} required/>
                        <label className="form-check-label"
                               htmlFor="publicAccessId">{Lang.updateSpace.access.public}</label>
                    </div>
                    <div className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="access"
                               checked={formData.access === PRIVATE_ACCESS}
                               onChange={handleOnChange}
                               id="privateId" value={PRIVATE_ACCESS}/>
                        <label className="form-check-label"
                               htmlFor="privateId">{Lang.updateSpace.access.private}</label>
                    </div>
                    <div className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="access"
                               checked={formData.access === RESTRICTED_ACCESS}
                               onChange={handleOnChange}
                               id="restrictedId" value={RESTRICTED_ACCESS}/>
                        <label className="form-check-label"
                               htmlFor="restrictedId">{Lang.updateSpace.access.restricted}</label>
                    </div>
                    <div className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="access"
                               checked={formData.access === ONLY_ME_ACCESS}
                               onChange={handleOnChange}
                               id="restrictedId" value={ONLY_ME_ACCESS}/>
                        <label className="form-check-label"
                               htmlFor="restrictedId">{Lang.updateSpace.access.onlyMe}</label>
                    </div>
                </FormRow>
                <div className="form-text text-muted text-left mb-2">
                    {Lang.updateSpace.access.information}
                </div>
            </React.Fragment>}


            <label className="form-check-label">{Lang.updateSpace.membership.text}</label>
            <FormRow border className='mx-0'>
                <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="membership"
                           checked={formData.membership === FREE_MEMBERSHIP}
                           onChange={handleOnChange}
                           id="eventTypeId" value={FREE_MEMBERSHIP} required/>
                    <label className="form-check-label"
                           htmlFor="eventTypeId">{FREE_MEMBERSHIP}</label>
                </div>

                <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="membership"
                           checked={formData.membership === REGULAR_MEMBERSHIP}
                           onChange={handleOnChange}
                           id="genericTypeId" value={REGULAR_MEMBERSHIP}/>
                    <label className="form-check-label"
                           htmlFor="genericTypeId">{REGULAR_MEMBERSHIP}</label>
                </div>

                <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="membership"
                           checked={formData.membership === SILVER_MEMBERSHIP}
                           onChange={handleOnChange}
                           id="genericTypeId" value={SILVER_MEMBERSHIP}/>
                    <label className="form-check-label"
                           htmlFor="genericTypeId">{SILVER_MEMBERSHIP}</label>
                </div>

                <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="membership"
                           checked={formData.membership === GOLD_MEMBERSHIP}
                           onChange={handleOnChange}
                           id="menuTypeId" value={GOLD_MEMBERSHIP}/>
                    <label className="form-check-label"
                           htmlFor="menuTypeId">{GOLD_MEMBERSHIP}</label>
                </div>
                <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="membership"
                           checked={formData.membership === PLATINUM_MEMBERSHIP}
                           onChange={handleOnChange}
                           id="menuTypeId" value={PLATINUM_MEMBERSHIP}/>
                    <label className="form-check-label"
                           htmlFor="menuTypeId">{PLATINUM_MEMBERSHIP}</label>
                </div>
                <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="membership"
                           checked={formData.membership === VIP_MEMBERSHIP}
                           onChange={handleOnChange}
                           id="menuTypeId" value={VIP_MEMBERSHIP}/>
                    <label className="form-check-label"
                           htmlFor="menuTypeId">{VIP_MEMBERSHIP}</label>
                </div>
                <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="membership"
                           checked={formData.membership === CLUB_MEMBERSHIP}
                           onChange={handleOnChange}
                           id="menuTypeId" value={CLUB_MEMBERSHIP}/>
                    <label className="form-check-label"
                           htmlFor="menuTypeId">{CLUB_MEMBERSHIP}</label>
                </div>
            </FormRow>



            {!isMenu && !isGroup && <React.Fragment>
                {/**** FEES ****/}
                <label className="form-check-label float-left"
                       htmlFor="freeOfChargesId">{Lang.updateSpace.fees.text}</label>
                <div style={{clear: 'both'}}/>
                <FormRow border className='text-left mx-0 mb-2'>
                    <div className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="fees"
                               checked={formData.fees === CHARGES_NONE}
                               onChange={handleOnBillable}
                               id="chargesNoneId" value={CHARGES_NONE}/>
                        <label className="form-check-label"
                               htmlFor="chargesNoneId">{Lang.updateSpace.fees.none}</label>
                    </div>
                    <div className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="fees"
                               checked={formData.fees === FREE_OF_CHARGE}
                               onChange={handleOnBillable}
                               id="freeOfChargesId" value={FREE_OF_CHARGE} required/>
                        <label className="form-check-label"
                               htmlFor="freeOfChargesId">{Lang.updateSpace.fees.free}</label>
                    </div>

                    <div className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="fees"
                               checked={formData.fees === BILLABLE}
                               onChange={handleOnBillable}
                               id="billableId" value={BILLABLE}/>
                        <label className="form-check-label"
                               htmlFor="billableId">{Lang.updateSpace.fees.billable}</label>
                    </div>
                </FormRow>

                {billable && <div>
                    <FormMarkdownTextarea className="form-control" name="feesDescription"
                                          label={Lang.updateSpace.feesDescription.text}
                                          placeholder={Lang.updateSpace.feesDescription.placeholder}
                                          id='feesDescriptionId'
                                          value={formData.feesDescription}
                                          onChange={handleOnChange} required/>
                </div>}

                {/**** HYGIENE ****/}
                <label className="form-check-label float-left mt-2"
                       htmlFor="hygieneNoneId">{Lang.updateSpace.hygiene.text}</label>
                <div style={{clear: 'both'}}/>
                <FormRow border className='text-left mx-0 mb-2'>
                    <div className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="hygiene"
                               checked={formData.hygiene === HYGIENE_NONE}
                               onChange={handleOnHygiene}
                               id="hygieneNoneId" value={HYGIENE_NONE} required/>
                        <label className="form-check-label"
                               htmlFor="hygieneNoneId">{Lang.updateSpace.hygiene.none}</label>
                    </div>

                    <div className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="hygiene"
                               checked={formData.hygiene === HYGIENE_LEGITIMATE}
                               onChange={handleOnHygiene}
                               id="hygieneLegitimateId" value={HYGIENE_LEGITIMATE}/>
                        <label className="form-check-label"
                               htmlFor="hygieneLegitimateId">{Lang.updateSpace.hygiene.legitimate}</label>
                    </div>
                </FormRow>

                {legitimate && <FormMarkdownTextarea
                    className="form-control" name="hygieneDescription"
                    label={Lang.updateSpace.hygieneDescription.text}
                    placeholder={Lang.updateSpace.hygieneDescription.placeholder}
                    id='hygieneDescriptionId'
                    value={formData.hygieneDescription}
                    onChange={handleOnChange} required/>}

                {/**** KALENDAR ***/}
                <NavigationRow className='mt-3'>
                    <NavigationGroup><h4 className='m-0'>{Lang.updateSpace.calendar}</h4></NavigationGroup>
                    <NavigationGroup>
                        <div className='box-aligned'>
                            <RoundedButton title={Lang.updateSpace.calendarNew.tooltip}
                                           onClick={handleOnNewDate}>
                                <Today/>
                            </RoundedButton>
                            <span className='small'>{Lang.updateSpace.calendarNew.text}</span>
                        </div>
                    </NavigationGroup>
                </NavigationRow>

                {renderCalendarDates()}

            </React.Fragment>}

            {/**** ADDRESS ***/}
            {!isMenu && !isGroup && <React.Fragment>
                <h4 className='mt-4 mb-2 text-left'>{Lang.updateSpace.address.text}</h4>

                <FormRow className='mb-2'>
                    <label className="form-check-label float-left"
                           htmlFor="locationId">{Lang.updateSpace.address.location.text}</label>
                    <input className="form-control" name="locationName"
                           placeholder={Lang.updateSpace.address.location.placeholder}
                           id='locationId'
                           value={addressData.locationName}
                           onChange={handleOnChangeAddress}/>
                </FormRow>

                <FormRow className='mb-2'>
                    <label className="form-check-label float-left"
                           htmlFor="streetId">{Lang.updateSpace.address.street}</label>
                    <input className="form-control" name="street"
                           placeholder={Lang.updateSpace.address.street}
                           id='streetId'
                           value={addressData.street}
                           onChange={handleOnChangeAddress} required/>
                </FormRow>

                <FormRowHeader>
                    <div className={`col-md-6`}>
                        <label className="form-check-label float-left"
                               htmlFor="areacodeId">{Lang.updateSpace.address.areacode}</label>
                        <input className="form-control" name="areacode"
                               placeholder={Lang.updateSpace.address.areacode}
                               id='areacodeId'
                               value={addressData.areacode}
                               onChange={handleOnChangeAddress} required/>
                    </div>
                    <div className={`col-md-6`}>
                        <label className="form-check-label float-left"
                               htmlFor="cityId">{Lang.updateSpace.address.city}</label>
                        <input className="form-control" name="city"
                               placeholder={Lang.updateSpace.address.city}
                               id='cityId'
                               value={addressData.city}
                               onChange={handleOnChangeAddress} required/>
                    </div>
                </FormRowHeader>

                <FormRow className='mb-2'>
                    <label className="form-check-label float-left"
                           htmlFor="countryId">{Lang.updateSpace.address.country}</label>
                    <input className="form-control" name="country"
                           placeholder={Lang.updateSpace.address.country}
                           id='countryId'
                           value={addressData.country}
                           onChange={handleOnChangeAddress} required/>
                </FormRow>
            </React.Fragment>}

            {/*** OPTIONAL ****/}
            {!isMenu && !isGroup && <Fragment>
                <NavigationRow className='mt-3'>
                    <NavigationGroup><h4 className='m-0'>{Lang.updateSpace.title.more}</h4></NavigationGroup>
                    <NavigationGroup>
                        <div className='box-aligned'>
                            <div className="form-check">
                                <input className="form-check-input" type="checkbox" name="showMore"
                                       checked={showMore}
                                       onChange={() => setShowMore(!showMore)}
                                       id="showMoreId"/>
                                <label className="small"
                                       htmlFor="showMoreId">{Lang.updateSpace.showMore}</label>
                            </div>
                        </div>
                    </NavigationGroup>
                </NavigationRow>
                <div className="text-muted small text-left mb-2">
                    {Lang.updateSpace.moreOptional}
                </div>
            </Fragment>}


            {showMore && <React.Fragment>
                <div className='mb-2'>
                    <FormMarkdownTextarea className="form-control" name="contactInformation"
                                          placeholder={Lang.updateSpace.contactInformation}
                                          value={formData.contactInformation}
                                          rows='4'
                                          maxLength='2056'
                                          onChange={handleOnChange}/>
                </div>

                <div className='mb-2'>
                    <FormMarkdownTextarea className="form-control" name="theVenue"
                                          placeholder={Lang.updateSpace.theVenue}
                                          value={formData.theVenue}
                                          rows='4'
                                          maxLength='2056'
                                          onChange={handleOnChange}/>
                </div>

                <div className='mb-2'>
                    <FormMarkdownTextarea className="form-control" name="accommodation"
                                          placeholder={Lang.updateSpace.accommodation}
                                          value={formData.accommodation}
                                          rows='4'
                                          maxLength='2056'
                                          onChange={handleOnChange}/>
                </div>

                <div className='mb-4'>
                    <FormMarkdownTextarea className="form-control" name="travelInformation"
                                          placeholder={Lang.updateSpace.travelInformation}
                                          value={formData.travelInformation}
                                          rows='4'
                                          maxLength='2056'
                                          onChange={handleOnChange}/>
                </div>
            </React.Fragment>}

            <FormRow className='mt-4 mb-2'>
                <Button block type="submit">
                    {Lang.updateSpace.save}
                </Button>
            </FormRow>
        </form>
    </div>
};

export default ManageSpaceForm;

